import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "src/@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "src/@/components/ui/select";
import { Input } from "src/@/components/ui/input";
import { Card, CardHeader, CardContent } from "src/@/components/ui/card";
import actionListaCanaisPorCliente from "src/actions/clientes/actionListaCanaisPorCliente";
import v2_actionConfigCrm from "src/actions/v2/crm/v2_actionConfigCrm";
import useAuth from "src/hooks/useAuth";
import { LabelFromAction } from "src/components/@dashboard/automacoes";
import { MultiSelect } from "src/@/components/ui/multi-select";
import { Switch } from "src/@/components/ui/switch";
import { Divider } from "@mui/material";
import HorarioAtendimentoAutomacao from "../components/HorarioAtendimentoAutomacao";
import { RowInput } from "src/components/forms/RowInput";
import AutomationOnlyExecute from "../components/AutomationOnlyExecute";
import { formatarCanaisParaSelect } from 'src/utils/canaisDisparo';

const CardInatividadeFunil = ({ actionState, changeActionState, actionPhase, diasSemana }) => {
    const { t } = useTranslation('automation');
    const { cliente } = useAuth();
    const [configCrm, setConfigCrm] = useState(null);
    const [canais, setCanais] = useState([]);

    const TIME_UNITS = [
        { label: t('actionCard.inatividadeFunil.timeUnitMinutes', 'Minuto(s)'), value: 1 },
        { label: t('actionCard.inatividadeFunil.timeUnitHours', 'Hora(s)'), value: 60 },
        { label: t('actionCard.inatividadeFunil.timeUnitDays', 'Dia(s)'), value: 1440 },
        { label: t('actionCard.inatividadeFunil.timeUnitWeeks', 'Semana(s)'), value: 10080 },
        { label: t('actionCard.inatividadeFunil.timeUnitMonths', 'Mês(es)'), value: 43200 },
    ];

    async function buscarListaCanais() {
        try {
            const response = await actionListaCanaisPorCliente(cliente._id);
            setCanais(response.data.data || []);
        } catch (error) {
            console.error("Erro ao buscar lista de canais:", error);
        }
    }

    useEffect(() => {
        buscarListaCanais();
    }, []);

    async function buscarConfigCrm(querys) {
        try {
            const result = await v2_actionConfigCrm(cliente._id, querys);
            if (result.data.data) {
                setConfigCrm(result.data.data);
            }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (actionState?.pipelineId) {
            buscarConfigCrm(`?pipeline_id=${actionState?.pipelineId}`);
        } else {
            buscarConfigCrm();
        }
    }, [actionState]);

    // useEffect(() => {
    //     const inactivityInMinutes = inactivityValue * inactivityUnit;
    //     changeActionState({ name: "timeInativity", value: inactivityInMinutes });
    // }, [inactivityValue, inactivityUnit]);

    let stagesFormatted = configCrm?.stages?.map((stage) => ({
        value: stage._id,
        label: stage.nome
    })) || [];

    let canaisFormatted = formatarCanaisParaSelect(canais);

    return (
        <Card className="w-full">
            <CardHeader>
                <LabelFromAction>{actionPhase.name}</LabelFromAction>
            </CardHeader>
            <CardContent>
                <div className="space-y-4 flex gap-2 flex-col">

                    <RowInput
                        isRequired={true}
                        label={t('actionCard.shared.channelLabel', 'Canal')}
                        info={t('actionCard.shared.channelInfo', 'Canais que estão atrelados ao chat')}
                        input={
                            <MultiSelect
                                options={canaisFormatted}
                                onValueChange={(e) =>
                                    changeActionState({ name: "chatChannels", value: e })
                                }
                                defaultValue={actionState?.chatChannels}
                                placeholder={t('actionCard.shared.channelPlaceholder', 'Selecionar canais')}
                                variant="inverted"
                                animation={2}
                                maxCount={3}
                            />
                        }
                    />

                    <RowInput
                        isRequired={true}
                        label={t('actionCard.inatividadeFunil.pipelineLabel', 'Selecionar Funil')}
                        input={<Select
                            value={actionState?.pipelineId}
                            defaultValue={actionState?.pipelineId}
                            onValueChange={(e) => {
                                changeActionState({ name: "pipelineId", value: e });
                                buscarConfigCrm(`?pipeline_id=${e}`);
                            }}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('actionCard.inatividadeFunil.pipelinePlaceholder', 'Selecione um funil')} />
                            </SelectTrigger>
                            <SelectContent>
                                {configCrm?.pipelines.map((pipe) => (
                                    <SelectItem key={pipe._id} value={pipe._id}>
                                        {pipe.nome}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>}
                    />


                    {actionState?.pipelineId && stagesFormatted && (
                        <RowInput
                            isRequired={true}
                            label={t('actionCard.inatividadeFunil.stagesPlaceholder', 'Selecionar estagios')}
                            input={<MultiSelect
                                options={stagesFormatted}
                                onValueChange={(e) => changeActionState({ name: "stage_ids", value: e })}
                                defaultValue={actionState?.stage_ids || []}
                                placeholder={t('actionCard.inatividadeFunil.stagesPlaceholder', 'Selecionar estagios')}
                                variant="inverted"
                                animation={2}
                                maxCount={3}
                            />}
                        />
                    )}

                    {actionState?.stage_ids?.length > 0 && (
                        <div>
                            <RowInput
                                isRequired={true}
                                label={t('actionCard.inatividadeFunil.inactivityTimeLabel', 'Tempo inatividade')}
                                input={<div className="flex gap-2">
                                    <Input
                                        type="number"
                                        min={actionState?.timeUnitInativity === 1 ? 5 : 1}
                                        value={actionState?.timeInativity}
                                        onChange={e => {
                                            let value = Number(e.target.value);
                                            // Se a unidade for minutos, não permite valor abaixo de 5
                                            if (actionState?.timeUnitInativity == 1 && value < 5) {
                                                value = 5;
                                            }
                                            changeActionState({ name: "timeInativity", value });
                                        }}
                                        className="w-1/2"
                                    />
                                    <Select
                                        value={actionState?.timeUnitInativity}
                                        onValueChange={(selectedValue) => {
                                            const unitValue = Number(selectedValue);
                                            changeActionState({ name: "timeUnitInativity", value: unitValue });
                                            if (unitValue === 1 && Number(actionState?.timeInativity) < 5) {
                                                changeActionState({ name: "timeInativity", value: 5 });
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="w-1/2">
                                            <SelectValue placeholder={t('actionCard.inatividadeFunil.unitPlaceholder', 'Selecione uma unidade')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TIME_UNITS.map((unit) => (
                                                <SelectItem key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>}
                            />

                        </div>
                    )}

                    <Divider className="border-dashed" />

                    <div className="flex items-center gap-2 mt-8">
                        <Switch
                            disabled
                            checked={actionState?.ignorePreviousActivities}
                            onCheckedChange={(checked) => changeActionState({ name: "ignorePreviousActivities", value: checked })}
                        />
                        <label className="text-sm font-medium">{t('actionCard.inatividadeFunil.ignorePreviousLabel', 'Desconsiderar leads com atividades anteriores')}</label>
                    </div>


                    <AutomationOnlyExecute
                        actionState={actionState}
                        changeActionState={changeActionState}
                    />

                    <Divider sx={{ mt: 4, mb: 2, borderStyle: 'dashed' }} />

                    <HorarioAtendimentoAutomacao
                        diasSemana={diasSemana}
                        customSchedule={actionState.scheduleCustom}
                        changeCustomScheduleService={value => {
                            changeActionState({ name: "scheduleCustom", value: value })
                        }}
                        scheduleService={actionState.schedule || []}
                        changeScheduleService={(index, key, value) => {
                            let newSchedule = [...actionState.schedule];
                            newSchedule[index] = {
                                ...newSchedule[index],
                                [key]: value,
                            };
                            changeActionState({
                                name: "schedule",
                                value: newSchedule,
                            });
                        }}

                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default CardInatividadeFunil;
